import React from 'react'
import { PageProps } from 'gatsby'
import WithTrans from 'presentation/components/Layout/WithTrans'
import SnackbarProvider from 'presentation/components/Snackbar'
import { Helmet } from 'react-helmet'
import 'css/global.css'
import 'css/gatsbyHeight100.css'
import 'css/fontFamily.css'
import { QueryParamProvider } from 'use-query-params'
import { Location, globalHistory } from '@reach/router'
import { LicenseInfo } from '@mui/x-license-pro'
// OEM / ホワイトラベル対応: /api/v1/public/brand から partner ブランドを取得して Context 配布
import {
  BrandProvider,
  useBrand,
  useBrandLoading,
} from 'scripts/brand/BrandContext'
// OEM: partner の brandColor を MUI テーマ(palette.primary)に反映し、color="primary" の要素も追従させる
import { BrandedThemeProvider } from 'scripts/brand/BrandedThemeProvider'
import SupportLink from 'presentation/components/SupportLink'
import { toSafeExternalUrl } from 'scripts/safeExternalUrl'

export const muiXDataGridPremiumLicense =
  '54bb137fe07a736733330c6e44ea1d4fTz05MzE4NyxFPTE3NTA5ODQ5ODgwMDAsUz1wcmVtaXVtLExNPXN1YnNjcmlwdGlvbixLVj0y'

LicenseInfo.setLicenseKey(muiXDataGridPremiumLicense)

export const BrandedHelmetTitle: React.FC = () => {
  const brand = useBrand()
  const brandLoading = useBrandLoading()
  // brand 解決前は LOCKED を含む title を出さない。非ブランドの中立 title にしてから差し替える。
  // OEM: 中立ブランド（NEUTRAL_BRAND）は productDisplayName が空文字列。partner ドメインで取得失敗
  // 確定したケースなので '認証画面'（中立 title）に倒し、'LOCKED 認証画面' を出さない。
  // productDisplayName が null/undefined（保険）でも同様に中立 title にする。
  // productDisplayName が非空（LOCKED='LOCKED' / partner='ACME' 等）はその名前を付ける。
  const title =
    brandLoading || !brand?.productDisplayName
      ? '認証画面'
      : `${brand.productDisplayName} 認証画面`
  // OEM: brand 解決後、安全な partner favicon があれば fallback の値に関わらず優先する。
  // 未設定 / 不正 URL では従来どおり、ホワイトラベル中のみ白画像へ差し替え、LOCKED は
  // link を出さず static/favicon.ico を使う。logoUrl は favicon 用途に流用しない。
  const safeFaviconUrl = brandLoading
    ? null
    : toSafeExternalUrl(brand.faviconUrl)
  const faviconUrl =
    safeFaviconUrl ||
    (!brandLoading && !brand.fallback ? '/favicon-blank.png' : null)
  return (
    <Helmet>
      <title>{title}</title>
      {faviconUrl && <link rel="icon" href={faviconUrl} />}
    </Helmet>
  )
}

const Layout = (props: PageProps) => {
  return (
    <BrandProvider>
      <BrandedHelmetTitle />
      <Helmet>
        <html lang="ja" />
        <link
          data-n-head="ssr"
          rel="stylesheet"
          href="https://fonts.googleapis.com/css?family=Noto+Sans+JP:400,500&amp;display=swap"
        />
      </Helmet>
      <BrandedThemeProvider>
        <Location>
          {({ location }) => (
            <QueryParamProvider
              location={location}
              reachHistory={globalHistory}
            >
              <SnackbarProvider>{props.children}</SnackbarProvider>
            </QueryParamProvider>
          )}
        </Location>
        <SupportLink />
      </BrandedThemeProvider>
    </BrandProvider>
  )
}

export default WithTrans(Layout)
